import classNames from "classnames";
import React, {
  ComponentProps,
  createContext,
  useContext,
  useRef,
  useEffect,
  useState,
  ReactNode,
} from "react";
import {twMerge} from "tailwind-merge";
import {AnimatePresence, motion} from "framer-motion";
import {Button} from "../Button";
import {InputValidityCheck} from "../InputField/InputValidityCheck";

export interface AccordionItemProps extends ComponentProps<"div"> {
  check?: boolean;
  itemIndex?: number;
  keepState?: boolean;
}

export interface AccordionProps extends React.ComponentProps<"div"> {
  activeItemIndex?: number;
  onActiveIndexChange?: (activeIndex?: number) => void;
}

export interface AccordionContextProps {
  activeIndex?: number;
  setActiveIndex: React.Dispatch<React.SetStateAction<number | undefined>>;
  onActiveIndexChange?: (activeIndex?: number) => void;
}

const AccordionItemContext = createContext({} as AccordionItemProps);
const AccordionContext = createContext({} as AccordionContextProps);

export const Accordion = ({
  children,
  activeItemIndex,
  onActiveIndexChange,
  ...restProps
}: AccordionProps) => {
  const [activeIndex, setActiveIndex] = useState<number>();

  useEffect(() => {
    if (activeItemIndex !== undefined) setActiveIndex(activeItemIndex);
  }, [activeItemIndex]);

  return (
    <div className="rounded-xl" {...restProps}>
      <AccordionContext.Provider
        value={{activeIndex, setActiveIndex, onActiveIndexChange}}
      >
        {React.Children.map<ReactNode, ReactNode>(children, (ele, index) => {
          if (!React.isValidElement(ele)) return;
          return React.cloneElement(
            ele as React.ReactElement<AccordionItemProps>,
            {
              itemIndex: index,
            },
          );
        })}
      </AccordionContext.Provider>
    </div>
  );
};

export const AccordionItemButton = ({
  children,
  className,
  ...restProps
}: ComponentProps<"button">) => {
  const {activeIndex, setActiveIndex, onActiveIndexChange} =
    useContext(AccordionContext);
  const {itemIndex, check} = useContext(AccordionItemContext);
  const open = activeIndex === itemIndex;
  return (
    <Button
      onClick={() => {
        setActiveIndex((prev) => {
          const index = prev !== itemIndex ? itemIndex : undefined;
          if (onActiveIndexChange) onActiveIndexChange(index);
          return index;
        });
      }}
      className={twMerge(
        "flex items-center gap-4 w-full h-full p-4 bg-white drop-shadow-md",
        "border-0 text-primaryBlue-700 hover:bg-slate-50 hover:shadow-none",
        "active:scale-100 rounded-none",
        className,
      )}
      {...restProps}
      color="light"
    >
      <div className="flex flex-1 gap-4">{children}</div>
      <InputValidityCheck className="w-6 text-base" inputValidity={check} />
      <div className="border-2 rounded-full p-2 border-primary-700 bg-primary-50">
        <div
          className={classNames(
            "w-2 transition-transform aspect-square",
            "border-b-2 border-r-2 border-primaryBlue-700",
            {"rotate-45 -translate-y-[15%] translate-x-0": open},
            {"-rotate-45 -translate-x-[15%]": !open},
          )}
        />
      </div>
    </Button>
  );
};

export const AccordionItemContent = ({
  children,
  className,
}: ComponentProps<"div">) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const {itemIndex, keepState} = useContext(AccordionItemContext);
  const {activeIndex} = useContext(AccordionContext);
  const [height, setHeight] = useState(0);
  const RObserver = window.ResizeObserver;
  const [observer] = useState(
    new RObserver(() => {
      setHeight(contentRef.current?.scrollHeight || 0);
    }),
  );

  useEffect(() => {
    if (!contentRef.current) return;
    observer.observe(contentRef.current);

    return () => observer.disconnect();
  }, [activeIndex]);

  const animate = activeIndex === itemIndex ? "expand" : "hidden";

  const keepChild = activeIndex === itemIndex || keepState;

  return (
    <AnimatePresence initial={false}>
      {keepChild && (
        <motion.div
          variants={{
            expand: {
              height: height,
            },
            hidden: {
              height: 0,
            },
          }}
          animate={animate}
          initial="hidden"
          exit={!keepState ? "hidden" : undefined}
          transition={{duration: 0.2, ease: "easeOut"}}
          className={twMerge("overflow-hidden", className)}
        >
          <div
            ref={contentRef}
            className="p-4 border-2 border-t-0 border-dashed bg-slate-100 rounded-b-2xl"
          >
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const AccordionItem = ({
  children,
  itemIndex = 0,
  check,
  keepState,
}: AccordionItemProps) => {
  return (
    <div className="flex flex-col">
      <AccordionItemContext.Provider value={{itemIndex, check, keepState}}>
        {children}
      </AccordionItemContext.Provider>
    </div>
  );
};

Accordion.ItemButton = AccordionItemButton;
Accordion.ItemContent = AccordionItemContent;
Accordion.Item = AccordionItem;
